import { ReactElement, useState } from "react";
import { Semantics } from "shared/components/drops/DropsBadge";
import {
  ActionButton,
  ActionButtons,
} from "shared/components/feedback/InfoMessage/ActionButtons";
import { ExpandableButton } from "shared/components/feedback/InfoMessage/ExpandableInfoMessage/ExpandableButton";
import { InfoMessageBaseStyling } from "shared/components/feedback/InfoMessage/style";
import { Box, BoxProps, Flex, Text } from "shared/components/Spor";
import { ThemeSemantic, useThemeSemantic } from "shared/theme/themeSemantic";
import { FCWC } from "shared/utils/FCWC";
import styled, { RuleSet } from "styled-components";

const ExpandableMessage = styled(Box)<{
  themeSemantic: ThemeSemantic;
  borderColorOverride?: string;
  $panelStyle?: RuleSet<object>;
}>`
  ${({ $panelStyle }) => $panelStyle || ""}
  ${({ themeSemantic, borderColorOverride }) =>
    InfoMessageBaseStyling(themeSemantic, borderColorOverride)};
`;

const ExpandablePanel = styled.div<{ $subtitleHeight: string }>`
  display: grid;
  transition: grid-template-rows 500ms;

  overflow: hidden;
  grid-template-rows: 0fr;
  opacity: 0;

  &[aria-hidden="false"] {
    min-height: ${({ $subtitleHeight }) => $subtitleHeight};
    grid-template-rows: 1fr;
    opacity: 1;
  }

  div {
    grid-row: 1 / span 2;
  }
`;

type InfoMessageProps = {
  semantics: Semantics;
  title: string | ReactElement;
  subTitle?: string | ReactElement;
  hideIcon?: boolean;
  actionButtons?: ActionButton[];
  initialOpenState?: boolean;
  hideSubtitleWhileOpen?: boolean;
  skeleton?: boolean;
  isProductionDeviation?: boolean;
  showExpand?: boolean;
  counter?: number;
  openPaddingY?: string;
} & Omit<BoxProps, "title">;

export const ExpandableInfoMessage: FCWC<InfoMessageProps> = ({
  children,
  semantics,
  title,
  subTitle,
  hideIcon,
  actionButtons = [],
  initialOpenState = false,
  hideSubtitleWhileOpen = true,
  skeleton = false,
  isProductionDeviation = false,
  showExpand = true,
  counter,
  openPaddingY = "12px",
  ...rest
}) => {
  const theme = useThemeSemantic(semantics);
  const [open, setopen] = useState(initialOpenState);

  /**
   * If subtitle is string then it is a single line (message)
   * If subtitle is ReactElement then it is two lines (title + message)
   */
  const subtitleHeight = typeof subTitle === "string" ? "21px" : "42px";

  return (
    <ExpandableMessage
      themeSemantic={theme}
      borderRadius="12px"
      p={hideIcon ? "18px" : "9px"}
      {...rest}
    >
      <ExpandableButton
        open={open}
        setopen={setopen}
        semantics={semantics}
        title={title}
        subTitle={hideSubtitleWhileOpen && open ? undefined : subTitle}
        skeleton={skeleton}
        hideIcon={hideIcon}
        showExpand={showExpand}
        counter={counter}
      />
      <ExpandablePanel
        id="collapsible_info_message_content"
        aria-hidden={!open}
        style={{
          color: theme.colorSemanticTextSecondary,
          marginLeft: hideIcon ? "6px" : "30px",
        }}
        $subtitleHeight={subtitleHeight}
      >
        <Flex flexDirection="column" gap="6px" py={open ? openPaddingY : "0px"}>
          {children}
          {isProductionDeviation && (
            <Text fontSize="inherit">
              OBS! Friskmelding og endring påvirker kun tog i dag, og hendelsen
              frakobles produksjonsavviket.
            </Text>
          )}
          {actionButtons?.length > 0 && (
            <ActionButtons
              semantics={semantics}
              actionButtons={actionButtons}
              rightAlign
              style={{
                paddingRight: "12px",
              }}
            />
          )}
        </Flex>
      </ExpandablePanel>
    </ExpandableMessage>
  );
};
